import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Separator, Tooltip, TooltipContent, TooltipTrigger, cn } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";
import { appOverviewPath, appOwningPath } from "@/lib/modules";
import { NAV_ENTRIES, filterNav } from "./route-registry";
import type { NavEntry } from "./route-registry";
import { LogoMark } from "@/components/brand/logo";

export const SIDEBAR_STORAGE_KEY = "erp.sidebar";

function getStoredCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "collapsed";
  } catch {
    return false;
  }
}

function storeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "collapsed" : "expanded");
  } catch {
    // Non-fatal.
  }
}

function isActivePath(entryPath: string, pathname: string): boolean {
  if (entryPath === "/") return pathname === "/";
  return pathname === entryPath || pathname.startsWith(`${entryPath}/`);
}

interface NavItemProps {
  entry: NavEntry;
  collapsed: boolean;
  active: boolean;
}

function NavItem({ entry, collapsed, active }: NavItemProps) {
  const { t, i18n } = useTranslation();
  const label = t(entry.labelKey);
  const Icon = entry.icon;
  const link = (
    <Link
      // Registry paths are plain strings; the registry stays the single
      // source of truth, so we opt out of the router's literal-path typing.
      to={entry.path as never}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm outline-none transition-colors",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring",
        active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
        collapsed && "justify-center px-2",
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      {/* Radix sides are physical; pick the one facing the content area. */}
      <TooltipContent side={i18n.dir() === "rtl" ? "left" : "right"}>{label}</TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const [collapsed, setCollapsed] = useState(getStoredCollapsed);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const entries = filterNav(NAV_ENTRIES, hasPermission);

  const toggle = (): void => {
    setCollapsed((current) => {
      const next = !current;
      storeCollapsed(next);
      return next;
    });
  };

  const toggleLabel = collapsed
    ? t("sidebar.expand", "Expand sidebar")
    : t("sidebar.collapse", "Collapse sidebar");

  return (
    <aside
      className={cn(
        "flex h-full shrink-0 flex-col border-e bg-sidebar text-sidebar-foreground transition-[width] duration-200",
        collapsed ? "w-14" : "w-64",
      )}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <div
        className={cn(
          "flex h-14 items-center gap-2 px-3",
          collapsed ? "justify-center px-1" : "justify-between",
        )}
      >
        {!collapsed && (
          <span className="flex min-w-0 items-center gap-2 ps-1">
            <LogoMark className="size-5 shrink-0" accent={false} />
            <span className="truncate text-sm font-semibold tracking-wide">{t("app.name")}</span>
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          {collapsed ? (
            <ChevronsRight className="size-4 rtl:rotate-180" aria-hidden="true" />
          ) : (
            <ChevronsLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
          )}
        </Button>
      </div>
      <nav
        aria-label={t("nav.main", "Main navigation")}
        className="flex-1 overflow-y-auto px-2 pb-4"
      >
        <ul className="flex flex-col gap-1">
          {entries.map((entry) => {
            if (!entry.children) {
              return (
                <li key={entry.path}>
                  <NavItem
                    entry={entry}
                    collapsed={collapsed}
                    active={isActivePath(entry.path, pathname)}
                  />
                </li>
              );
            }
            return (
              <li key={entry.path}>
                {collapsed ? (
                  <Separator className="mx-1 my-2 bg-sidebar-accent" />
                ) : (
                  // The group header is a link to the section's overview. It
                  // was a bare label before, which made "what is this part of
                  // the system for?" a question with nowhere to click — and
                  // for a section whose app is not installed, that page is the
                  // only thing that explains why its screens are quiet.
                  <Link
                    to={appOverviewPath(entry.app ?? appOwningPath(entry.path)) as never}
                    className={cn(
                      "block rounded-md px-3 pb-1 pt-4 text-xs font-medium uppercase tracking-wider",
                      "text-sidebar-foreground/60 outline-none transition-colors",
                      "hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-ring",
                    )}
                  >
                    {t(entry.labelKey)}
                  </Link>
                )}
                <ul className="flex flex-col gap-1">
                  {entry.children.map((child) => (
                    <li key={child.path}>
                      <NavItem
                        entry={child}
                        collapsed={collapsed}
                        active={isActivePath(child.path, pathname)}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
